/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useRef } from "react";
/* router */
import { useNavigate, Link } from "react-router-dom";
/* redux */
import { useDispatch, useSelector } from "react-redux";
/* hook form, yup */
import { useForm } from "react-hook-form";

/* material */
import { useTheme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import SignInIcon from "@mui/icons-material/Input";
import ForgotPwdIcon from "@mui/icons-material/LockOpen";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
// eslint-disable-next-line import/no-unresolved
import SSOLogin from "@regional/components/SSOLogin";
// eslint-disable-next-line import/no-unresolved
import gettingStartedConfig from "@regional/templates/GettingStarted/config";
/* app */
import GettingStartedDialog from "../../features/core/components/GettingStartedDialog";
import TermsConditionsCheckBox from "../../features/auth/components/TermsConditionsLink";
import ReCaptchaComp from "../../features/auth/components/ReCaptcha";
import LocalLoader from "../../components/Loaders/Local";
import FormAlert from "../../components/Form/Field/FormAlert";

import { getLogin, logout } from "../../features/auth/slices/auth.slice";
import {
  useLoginMutation,
  useCaptchaConfigMutation,
} from "../../api/auth.api.slice";
import { resetSessionData } from "../../features/auth/slices/userSession.slice";
import { showNotification } from "../../features/core/slices/notification.slice";
import { isSSOEnabled, notifyError } from "../../utils/helpers";
import { errorMap } from "../Register";
import classes from "./Login.module.css";

const showPreLoginAnnouncement = Boolean(
  gettingStartedConfig.preLoginAnnouncement?.show
);

function LoginPage() {
  const muiTheme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState("");
  const [errorCode, setErrorCode] = useState(null);
  const [captchaConfigData, setCaptchaConfigData] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const captchaRef = useRef(null);
  const [login, { isLoading }] = useLoginMutation();
  const [captchaConfig] = useCaptchaConfigMutation();

  const { tenantId, postLoginRoute } = useSelector(
    (state) => state.userSession.info
  );

  const {
    register,
    handleSubmit,
    control,
    resetField,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
  });

  useEffect(() => {
    dispatch(logout());
    const getCaptchaConfig = async () => {
      try {
        const data = await captchaConfig().unwrap();
        if (data) {
          setCaptchaConfigData(data);
        }
      } catch (err) {
        notifyError(dispatch, showNotification, err);
      }
    };
    if (!isSSOEnabled) getCaptchaConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* a captcha token is single use, so it has to be re-generated before the
     user can submit again, otherwise the next attempt fails on verification */
  const resetCaptcha = () => {
    if (!captchaConfigData?.captchaLogin) return;
    captchaRef.current?.reset();
    resetField("captchaToken");
  };

  const doLogin = async (values) => {
    const res = await login(values);
    if (res.error) {
      resetCaptcha();
      const errorStatus = res.error.data?.code;
      setErrorCode(errorStatus);
      setLoginError(
        res.error.data?.message
          ? res.error.data.message
          : "Error while logging in, please try again later."
      );
    } else {
      setErrorCode(null);
      await dispatch(getLogin());
      const { _id } = res?.data?.token?.currentTenant || {};
      let navigateTo = postLoginRoute ?? "/licenses";
      if (navigateTo.includes("applications") && tenantId !== _id)
        navigateTo = "/applications";
      dispatch(resetSessionData());
      navigate(navigateTo, { replace: true });
    }
  };
  const onError = (error) => {
    const errItem = Object.keys(error);
    if (errItem.includes("username") || errItem.includes("password"))
      setLoginError(errorMap.common);
    else if (error.termsConditions) setLoginError(errorMap.termsConditions);
    else if (error.captchaToken) setLoginError(errorMap.captchaToken);
  };
  if (isSSOEnabled)
    return (
      <>
        {showPreLoginAnnouncement && (
          <GettingStartedDialog variant="preLogin" />
        )}
        <Grid
          container
          className={classes.loginGrid}
          sx={{
            gap: 2,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <SSOLogin />
        </Grid>
      </>
    );

  return (
    <div>
      <Grid
        container
        direction="row"
        className={classes.loginGrid}
        sx={{ justifyContent: "center", alignItems: "stretch" }}
      >
        <Grid
          className={`${classes.flexCenter} ${classes.loginForm}`}
          size={{ xs: 12, sm: 8 }}
        >
          <Card className={classes.card} raised>
            <form onSubmit={handleSubmit(doLogin, onError)} noValidate>
              <CardHeader
                title="Sign-in"
                slotProps={{ title: { component: "h1" } }}
                className={classes.cardHeader}
              >
                Sign-in
              </CardHeader>
              <CardContent>
                <TextField
                  label="Email"
                  id="username"
                  error={errors.username !== undefined}
                  name="username"
                  required
                  type="text"
                  margin="normal"
                  variant="outlined"
                  autoComplete="email"
                  helperText={errors.username ? errors.username.message : ""}
                  {...register("username", {
                    required: "Email is required",
                    maxLength: {
                      value: 100,
                      message: "Email must not exceed 100 characters",
                    },
                  })}
                  fullWidth
                />
                <TextField
                  label="Password"
                  id="password"
                  name="password"
                  required
                  error={errors.password !== undefined}
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  variant="outlined"
                  autoComplete="current-password"
                  helperText={errors.password ? errors.password.message : ""}
                  {...register("password", {
                    required: "Password is required",
                    maxLength: {
                      value: 50,
                      message: "password must not exceed 50 characters",
                    },
                  })}
                  fullWidth
                  slotProps={{
                    input: {
                      endAdornment: (
                        <IconButton
                          aria-label="Toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                      "data-testid": "password",
                    },
                  }}
                />
              </CardContent>
              <CardActions
                disableSpacing
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Grid
                  container
                  sx={{ justifyContent: "center", alignItems: "center" }}
                >
                  <TermsConditionsCheckBox register={register} type="signIn" />
                </Grid>
                {captchaConfigData?.captchaLogin && (
                  <Grid
                    container
                    sx={{ justifyContent: "center", alignItems: "center" }}
                  >
                    <ReCaptchaComp
                      name="captchaToken"
                      captchaRef={captchaRef}
                      control={control}
                      theme={muiTheme.palette.mode}
                      error={false}
                    />
                  </Grid>
                )}
                <Grid
                  container
                  sx={(theme) => ({
                    justifyContent: "center",
                    mt: "0.5em",
                    "& .MuiButton-root": {
                      my: 1,
                      "& .MuiSvgIcon-root": { mr: "0.2em" },
                    },
                    [theme.breakpoints.down("sm")]: { flexDirection: "column" },
                  })}
                >
                  <Button variant="contained" type="submit">
                    <SignInIcon aria-hidden="true" />
                    SIGN IN
                  </Button>
                  <Button
                    variant="contained"
                    component={Link}
                    to="/forgot-password"
                  >
                    <ForgotPwdIcon aria-hidden="true" />
                    FORGOT PASSWORD
                  </Button>
                </Grid>
              </CardActions>
            </form>
            {loginError && (
              <Grid
                container
                className={classes.errorItem}
                sx={{ justifyContent: "center" }}
              >
                <Grid size={11}>
                  <FormAlert
                    severity="error"
                    message={
                      <span>
                        {loginError}
                        {errorCode === 403 && (
                          <>
                            <br />
                            <Link
                              to="/unlockAccount"
                              style={{
                                textDecoration: "underline",
                                fontWeight: "bold",
                              }}
                            >
                              <ForgotPwdIcon
                                aria-hidden="true"
                                sx={{ mr: 0.5, mb: -0.75 }}
                              />
                              Unlock Account
                            </Link>
                          </>
                        )}
                      </span>
                    }
                  />
                </Grid>
              </Grid>
            )}
            <LocalLoader progress={isLoading} />
          </Card>
        </Grid>
      </Grid>
      {showPreLoginAnnouncement && <GettingStartedDialog variant="preLogin" />}
    </div>
  );
}

export default LoginPage;
